import { createElement, useEffect, useRef } from 'react';
import { DraggableTabProps } from '..';
import { useReactDndConstructs } from '../../../../dnd/ReactDndContext';
import { useDesignTokens } from '../../../../theme';
import { getDndStyleTokens } from '../../../../style/reactDnd/DndStyleTokens';
import { useEarcon, EarconName } from '../../../../hooks/useEarcon';

import cx from 'classnames';
import cn from './Dnd.css.json';
import './Dnd.css';

export const DraggableTab = (props: DraggableTabProps) => {
  const dropRef = useRef<HTMLDivElement>(null);
  const { useDrag, useDrop, getEmptyImage } = useReactDndConstructs();
  const earcon = useEarcon({ source: EarconName.InputFull });

  const [{ isDragging }, drag, preview] = useDrag({
    // NOTE: type is now a required prop and is decoupled from item in react-dnd(v14.0.0)
    // @see - https://github.com/react-dnd/react-dnd/releases/tag/v14.0.0
    type: props.type,
    item: {
      id: props.id,
      type: props.type,
      index: props.index,
      dndPrefix: props.dndPrefix,
    },
    canDrag: props.allowDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (draggedItem: any | undefined, monitor) => {
      const dropResult = monitor.getDropResult();
      if (!dropResult) {
        earcon.play();
        props.onFocus?.();
      } else if (dropResult && draggedItem && dropResult.type !== draggedItem.type) {
        props.onDragEnd?.({
          draggdItem: draggedItem,
          dragIndex: draggedItem.index,
          monitor,
        });
      }
    },
  });

  const [{ styleClassName }, drop] = useDrop({
    accept: props.acceptDropTypes!,
    canDrop: () => props.allowDrop,
    drop: (item: DraggableTabProps, monitor) => {
      if (!monitor.didDrop()) {
        if (props.dndPrefix === item.dndPrefix) {
          if (item.index !== props.index) props.reorderTabs?.(item.index, props.index);
        } else {
          props.onDrop?.({
            droppedItem: item,
            dropIndex: props.index,
            monitor,
          });
        }
        return { type: props.type };
      }
    },

    collect(monitor) {
      const dragIndex = monitor.getItem()?.index;
      const dragDndPrefix = monitor.getItem()?.dndPrefix;
      let styleClass = '';

      if (monitor.isOver() && monitor.canDrop()) {
        if (
          props.droppableContent.length &&
          props.droppableContent.includes(monitor.getItem()?.type)
        ) {
          styleClass = cn.dragOver;
        } else if (!isDragging && (dragIndex > props.index || dragDndPrefix !== props.dndPrefix)) {
          styleClass = cx(props.orientation === 'vertical' ? cn.vDropLine : cn.dropLine);
        } else if (dragIndex < props.index && !isDragging) {
          styleClass = cx(
            props.orientation === 'vertical' ? cn.vDropLine : cn.dropLine,
            cn.placeAtEnd
          );
        }
      }

      return {
        styleClassName: styleClass,
      };
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview, getEmptyImage]);

  const dt = useDesignTokens();
  const dndTokens = getDndStyleTokens(dt);

  drag(drop(dropRef));

  return (
    <div
      ref={dropRef}
      style={dndTokens}
      className={cx(props.allowDrag && cn.draggable, isDragging && cn.draggedItem, styleClassName)}
    >
      {props.children}
    </div>
  );
};
